import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
    const years = Math.floor(months / 12);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
}

const truncStyle = (lines) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
});

// Strip markdown syntax to plain text
function stripMarkdown(md) {
    if (!md) return '';
    return md
        .replace(/^#{1,6}\s+/gm, '')       // headings
        .replace(/(\*{1,3}|_{1,3})(.*?)\1/g, '$2') // bold/italic
        .replace(/!\[.*?\]\(.*?\)/g, '')    // images
        .replace(/\[([^\]]*)\]\(.*?\)/g, '$1') // links
        .replace(/`{1,3}[^`]*`{1,3}/g, '') // inline/fenced code
        .replace(/^\s*[-*+]\s+/gm, '')      // unordered list markers
        .replace(/^\s*\d+\.\s+/gm, '')      // ordered list markers
        .replace(/^\s*>\s+/gm, '')          // blockquotes
        .replace(/\$\$?[^$]*\$\$?/g, '')   // LaTeX
        .replace(/\|.*\|/g, '')             // table rows
        .replace(/---+/g, '')               // horizontal rules
        .replace(/\n{2,}/g, ' ')            // collapse blank lines
        .replace(/\n/g, ' ')               // remaining newlines
        .replace(/\s{2,}/g, ' ')           // collapse whitespace
        .trim();
}

function ProjectCard({ project, colClass = "col-12", showStatus = false }) {
    const isSingleCol = colClass === "col-12";
    const bodyPreview = isSingleCol && project.body ? stripMarkdown(project.body).slice(0, 200) : '';
    const hasBanner = !!project.picture_link;
    const [hovered, setHovered] = useState(false);

    return (
        <div className={colClass} key={project.id}>
            <div
                className={`project-card-custom zoom${isSingleCol ? ' project-card-wide' : ''}`}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{ position: 'relative', overflow: 'hidden' }}
            >
                {/* Banner: thin strip by default, fades to full background on hover */}
                {hasBanner && (
                    <>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: hovered ? '100%' : 4,
                            backgroundImage: `url(${project.picture_link})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            opacity: hovered ? 0.2 : 1,
                            transition: 'height 300ms ease, opacity 300ms ease',
                            pointerEvents: 'none',
                            borderRadius: hovered ? 'inherit' : '6px 6px 0 0',
                        }} />
                    </>
                )}
                <Link to={`/projects/${project.id}`} style={{ position: 'relative', zIndex: 1 }}>
                    <h3
                        style={{
                            color: 'var(--ink)',
                            fontWeight: 800,
                            fontSize: '1.3em',
                            margin: 0,
                            ...truncStyle(2),
                        }}
                    >
                        {project.title}
                    </h3>
                    {project.subtitle && (
                        <h5 style={{ margin: '10px 0 0', ...truncStyle(2) }}>
                            {project.subtitle}
                        </h5>
                    )}
                    {project.technology && (
                        <p
                            style={{
                                color: 'var(--brand)',
                                fontWeight: 600,
                                fontSize: '0.7em',
                                lineHeight: '1.4',
                                margin: '14px 0 0',
                                ...truncStyle(2),
                            }}
                        >
                            {project.technology}
                        </p>
                    )}
                    {project.skill && (
                        <p
                            style={{
                                color: 'var(--brand)',
                                fontWeight: 600,
                                fontSize: '0.7em',
                                lineHeight: '1.4',
                                margin: '8px 0 0',
                                ...truncStyle(2),
                            }}
                        >
                            {project.skill}
                        </p>
                    )}
                    {bodyPreview && (
                        <p
                            style={{
                                color: 'var(--muted)',
                                fontSize: '0.55em',
                                lineHeight: '1.5',
                                margin: '12px 0 0',
                                ...truncStyle(3),
                            }}
                        >
                            {bodyPreview}
                        </p>
                    )}
                    <p
                        style={{
                            color: 'var(--muted)',
                            fontSize: '0.55em',
                            margin: '10px 0 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        {project.view_count > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <VisibilityIcon style={{ fontSize: 13 }} />
                                {project.view_count.toLocaleString()}
                            </span>
                        )}
                        {project.like_count > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <FavoriteIcon style={{ fontSize: 12, color: '#ba3f9c' }} />
                                {project.like_count.toLocaleString()}
                            </span>
                        )}
                        {showStatus && (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                color: project.publish ? '#16a34a' : 'var(--muted)',
                            }}>
                                <span style={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    backgroundColor: project.publish ? '#16a34a' : 'var(--muted)',
                                    opacity: project.publish ? 1 : 0.5,
                                }} />
                                {project.publish ? 'Published' : 'Private'}
                            </span>
                        )}
                        {project.created_at && (
                            <span>{timeAgo(project.created_at)}</span>
                        )}
                        {project.updated_at && project.created_at &&
                            Math.abs(new Date(project.updated_at) - new Date(project.created_at)) > 60000 && (
                            <span>· updated {timeAgo(project.updated_at)}</span>
                        )}
                    </p>
                </Link>
            </div>
        </div>
    );
}

export default ProjectCard;
